import { Container } from '@/components/Container';
import { AppLink } from '@/components/AppLink';
import { DraftMarker } from '@/components/DraftMarker';
import { Eyebrow } from '@/components/Eyebrow';

// Written only from facts the client has already approved: founded 1941 by nine
// founder members; non-commercial society and public trust; affiliated
// institutes in India and abroad; syllabi for nine faculties; Open Merit
// Examinations at approved centres; and the scope printed on the ISO
// certificate ("affiliation certification, student final exam, marksheet and
// course certification").
const STEPS = [
  {
    title: 'An institute affiliates',
    text: 'Technical and vocational institutes in India and abroad affiliate with ITES to teach its courses.',
    link: { label: 'Affiliation rules', to: '/affiliation' },
  },
  {
    title: 'ITES sets the syllabus',
    text: 'Each of the nine faculties has an ITES syllabus listing its courses and what is examined.',
    link: { label: 'Courses & syllabi', to: '/courses' },
  },
  {
    title: 'Students sit the exam',
    text: 'The institute sponsors its students for the ITES Open Merit Examination, held at an approved exam centre.',
    link: { label: 'Examinations', to: '/examinations' },
  },
  {
    title: 'ITES certifies',
    text: "ITES issues each student's marksheet and course certificate.",
  },
] as const;

export function WhatIsITES() {
  return (
    <section aria-labelledby="what-title" className="border-t border-rule bg-white">
      <Container className="py-section">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] items-end gap-x-[clamp(36px,5vw,72px)] gap-y-6">
          <div>
            <Eyebrow>What ITES is</Eyebrow>
            <h2 id="what-title" className="m-0 font-serif text-h2 font-semibold text-balance text-navy-900">
              ITES examines and certifies. Affiliated institutes teach.
            </h2>
          </div>
          <p className="m-0 max-w-[36em] text-[1.125rem] leading-[1.7] text-ink-2">
            Founded in Mumbai in 1941 by nine founder members, ITES is a non-commercial society and public trust. It sets
            the syllabi for technical and vocational courses and conducts the examinations for them, while the teaching
            is done by its affiliated institutes.
          </p>
        </div>
        <DraftMarker className="mt-5">
          DRAFT · plain-language wording (this section and the hero line), written from the approved facts; ITES to confirm
        </DraftMarker>

        <ol className="m-0 mt-10 grid list-none grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-x-8 gap-y-8 p-0">
          {STEPS.map((s, i) => (
            <li key={s.title} className="border-t border-rule-strong pt-4">
              <p className="m-0 font-mono text-[0.8125rem] text-teal-700">{String(i + 1).padStart(2, '0')}</p>
              <h3 className="mt-2 mb-0 font-serif text-[1.375rem] leading-[1.25] font-semibold text-navy-900">{s.title}</h3>
              <p className="mt-2 mb-0 text-[1rem] leading-[1.55] text-ink-2">{s.text}</p>
              {'link' in s && (
                <AppLink href={s.link.to} className="tap mt-3 inline-block font-semibold underline underline-offset-[3px]">
                  {s.link.label} →
                </AppLink>
              )}
            </li>
          ))}
        </ol>
      </Container>
    </section>
  );
}
